/**
 * Read endpoints shared by the panel and the bot (specs/c2-kontratlar.md 2.3, 2.4.8,
 * 2.11). The shapes are the bot's existing schemas (apps/telegram/src/api/types.ts)
 * plus additive fields; responses may carry more fields, clients drop what they do
 * not know.
 */
import { z } from 'zod';
import { approvalStatusSchema, configAgentIdSchema, countSchema, executionStatusSchema, idStringSchema, isoDateTimeSchema, killSwitchLevelSchema, microUsdSchema, sha256Schema, tierSchema, verifierStatusSchema, } from '../ids.js';
import { listLimitSchema } from './common.js';
/** GET /api/v1/healthz: no identity, no content. */
export const healthzResponseSchema = z.object({
    ok: z.boolean(),
    db: z.boolean(),
    listener: z.boolean(),
});
/** GET /api/v1/me. Authority is read again at every decision; this is for display. */
export const meResponseSchema = z.object({
    humanId: z.string().min(1),
    displayName: z.string().min(1),
    role: z.enum(['owner', 'meeting_lead', 'engineer']),
    canDecideT3: z.boolean(),
    canDecideT4: z.boolean(),
    surface: z.enum(['panel', 'bot']),
});
/** GET /api/v1/status (bot SystemStatus plus pendingExecutions). */
export const systemStatusSchema = z.object({
    killSwitch: z.object({
        level: killSwitchLevelSchema,
        reason: z.string().nullable(),
        updatedAt: isoDateTimeSchema.nullable(),
        updatedBy: z.string().nullable(),
    }),
    /** policies.yaml executor.dry_run OR system_state executor.dry_run (K8). */
    dryRun: z.boolean(),
    pendingApprovals: countSchema,
    openTasks: countSchema,
    runningRuns: countSchema,
    /** Real spend of the UTC day (notional excluded). */
    todaySpendMicroUsd: microUsdSchema,
    lastRunAt: isoDateTimeSchema.nullable(),
    /**
     * Approved and scheduled approvals younger than executor.max_approval_age_hours: what
     * the executor applies once the switch is back to none. /devam shows it before deciding.
     */
    pendingExecutions: countSchema,
});
/** GET /api/v1/budget (bot BudgetSummary; sources in 2.4.8). */
export const budgetSummarySchema = z.object({
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    monthSpentMicroUsd: microUsdSchema,
    monthCapMicroUsd: microUsdSchema,
    daySpentMicroUsd: microUsdSchema,
    dayCapMicroUsd: microUsdSchema,
    genesisMicroUsd: microUsdSchema,
    claudeNotionalMicroUsd: microUsdSchema,
    agents: z.array(z.object({
        agentId: z.string().min(1),
        spentMicroUsd: microUsdSchema,
        capMicroUsd: microUsdSchema.nullable(),
        notional: z.boolean(),
    })),
});
/** Longest previewText of an approval resource. */
export const APPROVAL_PREVIEW_MAX = 1500;
/**
 * v0.1.213 (review of #82): a site_publish preview holds up to 3 new texts of SITE_REPLACE_MAX whole, with the summary,
 * the page and the start of each removed text, so its cap is higher; the Telegram card still fits in 4096.
 */
export const SITE_PREVIEW_MAX = 2700;
/**
 * GET /api/v1/approvals/{id} (bot Approval). previewText: customer_reply is reply_en, an
 * empty line and reply_tr; others preview.summary_tr and the fields. agentId is the
 * config id, null for a human proposal. Growth approvals are not listed in C2.
 */
export const approvalResourceSchema = z.object({
    id: idStringSchema,
    tier: tierSchema,
    actionType: z.string().min(1),
    title: z.string().min(1),
    reason: z.string(),
    agentId: configAgentIdSchema.nullable(),
    confidence: z.enum(['high', 'low']).nullable(),
    verifierStatus: verifierStatusSchema,
    customerWaiting: z.boolean(),
    previewText: z.string().max(SITE_PREVIEW_MAX).nullable(),
    /** v0.1.46: where the proposal came from, one line for the Telegram card (no addresses). */
    sourceLine: z.string().max(240).nullable(),
    /** v0.1.55: for a customer reply, whether it answers an e-mail (Gönder sends it from Gmail) or an Etsy message. */
    replyChannel: z.enum(['email', 'etsy']).nullable(),
    /** v0.1.180: the Denetçi's objection, when it objected (the Telegram card shows it above the draft). */
    verifierNote: z.string().max(600).nullable().optional(),
    /** v0.1.188: what Sercan pastes by hand (listing text, social post) once an approval is handed over to him. */
    copyText: z.string().max(3500).nullable().optional(),
    executionStatus: executionStatusSchema,
    payloadHash: sha256Schema,
    status: approvalStatusSchema,
    createdAt: isoDateTimeSchema,
    expiresAt: isoDateTimeSchema,
});
/** GET /api/v1/approvals?status=pending&limit=N. */
export const approvalListSchema = z.object({
    items: z.array(approvalResourceSchema),
    total: countSchema,
});
export const approvalListQuerySchema = z.strictObject({
    status: z.literal('pending').default('pending'),
    limit: listLimitSchema.default(20),
});
/** GET /api/v1/unknown-questions?status=open&limit=N. question = question_tr ?? question_en (never empty). */
export const unknownQuestionListSchema = z.object({
    items: z.array(z.object({
        id: idStringSchema,
        question: z.string().min(1),
        customerWaiting: z.boolean(),
        createdAt: isoDateTimeSchema,
    })),
    total: countSchema,
});
/**
 * GET /api/v1/sekreter (v0.1.26): the Sekreter's last sent list (twin.sekreter_digests, newest row).
 * Before the first digest: null digestId, sentAt and reason with an empty items list.
 */
export const sekreterItemSchema = z.object({
    key: z.string().min(1),
    text: z.string().min(1),
    place: z.string(),
});
/** An item Sercan (or a release seed) wrote himself: twin.sekreter_items (v0.1.28). */
export const sekreterManualItemSchema = z.object({
    itemId: idStringSchema,
    code: z.string().min(2).max(64),
    title: z.string().min(1).max(200),
    detail: z.string().max(600).nullable(),
    place: z.string().max(120).nullable(),
    createdBy: z.string().min(1),
    createdAt: isoDateTimeSchema,
    doneAt: isoDateTimeSchema.nullable(),
    doneBy: z.string().nullable(),
});
export const sekreterDigestSchema = z.object({
    digestId: idStringSchema.nullable(),
    sentAt: isoDateTimeSchema.nullable(),
    reason: z.enum(['first', 'changed', 'morning']).nullable(),
    items: z.array(sekreterItemSchema),
    /** Open items first, then those done in the last two weeks. */
    manual: z.array(sekreterManualItemSchema),
});
/** POST /sekreter/items: what Sercan types in the panel. */
export const sekreterItemBodySchema = z.strictObject({
    title: z.string().trim().min(3).max(200),
    detail: z.string().trim().min(1).max(600).optional(),
    place: z.string().trim().min(1).max(120).optional(),
});
export const sekreterItemResponseSchema = z.strictObject({
    itemId: idStringSchema,
    code: z.string(),
    status: z.enum(['open', 'done']),
});
export const unknownQuestionListQuerySchema = z.strictObject({
    status: z.literal('open').default('open'),
    limit: listLimitSchema.default(20),
});
//# sourceMappingURL=resources.js.map